import { FormControl, FormGroup } from "@angular/forms";

/**
 * Form controls for Coupon Details section
 * - Internal Name (max 255 chars)
 * - Coupon Code (max 20 chars, alphanumeric + special chars)
 */
export interface CouponDetailsForm {
  name: FormControl<string>;
  code: FormControl<string>;
}

/**
 * Form controls for Discount Configuration section
 * Handles all discount types: subtotal %, subtotal $, shipping %, shipping $, product discount
 */
export interface DiscountConfigForm {
  discountType: FormControl<string>;
  amount: FormControl<number | null>;
  maxPercentageDiscount: FormControl<number | null>;
  subtotalDiscountAppliesToRegularPrice: FormControl<boolean>;
  productId: FormControl<number | null>;
  productGroupId: FormControl<number | null>;
  productDiscountNotAppliesWhenItHasSalePrice: FormControl<boolean>;
  discountLimitsMode: FormControl<"limited" | "unlimited">;
}

/**
 * Form controls for Recurring Discounts section
 * - Apply forever / Apply for N cycles / One-time only
 * - Is Stackable
 * - Expiration Date
 */
export interface RecurringDiscountForm {
  recurringMode: FormControl<"forever" | "cycles" | "once">;
  maxCyclesPerScheduledOrder: FormControl<number | null>;
  isStackable: FormControl<boolean>;
  expirationDate: FormControl<Date | null>;
  applyExpirationDateToExisting: FormControl<boolean>;
}

/**
 * Form controls for Conversion Limits section
 * - Maximum Uses Per Customer (Unlimited, 1-5, Custom)
 * - Maximum Overall Uses (site-wide)
 */
export interface ConversionLimitsForm {
  maxAssignmentsPerCustomerMode: FormControl<string>; // "Unlimited", "1"-"5", "Custom"
  maxAssignmentsPerCustomer: FormControl<number | null>;
  maxAssignmentsPerSite: FormControl<number | null>;
}

/**
 * Form controls for Advanced Options section
 * May require subscription tier upgrade to enable
 */
export interface AdvancedOptionsForm {
  // Cycles constraints
  minScheduledOrderCycles: FormControl<number | null>;
  maxScheduledOrderCycles: FormControl<number | null>;

  // Shipping Address constraints
  country: FormControl<string | null>;
  state: FormControl<string | null>;
  city: FormControl<string | null>;
  postcode: FormControl<string | null>;

  // Items constraints
  minUnits: FormControl<number | null>;
  minSubtotal: FormControl<number | null>;
  minWeight: FormControl<number | null>;
  maxWeight: FormControl<number | null>;
  weightUnitType: FormControl<string | null>;

  // Customer targeting
  customerMode: FormControl<"all" | "include" | "exclude">;
  includeCustomerEmails: FormControl<string | null>;
  excludeCustomerEmails: FormControl<string | null>;
}

/**
 * Complete Coupon Form structure
 * Organized into logical sections matching the UI
 */
export interface CouponForm {
  details: FormGroup<CouponDetailsForm>;
  discount: FormGroup<DiscountConfigForm>;
  recurring: FormGroup<RecurringDiscountForm>;
  conversionLimits: FormGroup<ConversionLimitsForm>;
  advancedOptions: FormGroup<AdvancedOptionsForm>;
}

/**
 * Recurring discount mode options
 */
export type RecurringDiscountMode = "forever" | "cycles" | "once";

/**
 * Discount limits mode options
 */
export type DiscountLimitsMode = "limited" | "unlimited";

/**
 * Customer targeting mode options
 */
export type CustomerTargetingMode = "all" | "include" | "exclude";

/**
 * Max assignments per customer dropdown options
 */
export const MAX_ASSIGNMENTS_PER_CUSTOMER_OPTIONS = [
  "Unlimited",
  "1",
  "2",
  "3",
  "4",
  "5",
  "Custom",
] as const;

/**
 * Recurring discount cycles dropdown options
 */
export const RECURRING_DISCOUNT_CYCLES_OPTIONS = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "Custom",
] as const;
